import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Upload,
  Download,
  Trash2,
  Loader2,
  ImageIcon,
  ZoomIn,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ExtendedLead } from './types';

interface Foto {
  id: string;
  nome_arquivo: string;
  nome_original: string;
  tipo_arquivo: string;
  tamanho_bytes: number;
  largura_px?: number;
  altura_px?: number;
  url_publica: string;
  descricao?: string;
  created_at: string;
}

interface FotosProps {
  leadData: ExtendedLead;
  onUpdate: () => void;
}

export default function Fotos({ leadData, onUpdate }: FotosProps) {
  const [fotos, setFotos] = useState<Foto[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fotoToDelete, setFotoToDelete] = useState<Foto | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [descricao, setDescricao] = useState('');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [currentFotoIndex, setCurrentFotoIndex] = useState(0);
  const { toast } = useToast();

  const fetchFotos = async () => {
    try {
      setLoading(true);
      console.log('📸 fetchFotos - Buscando fotos para lead:', leadData.id);

      const response = await fetchWithAuth(`/api/cliente-fotos/${leadData.id}`);
      console.log('📸 fetchFotos - Resposta recebida:', { status: response.status, ok: response.ok });

      if (!response.ok) {
        // Tentar ler como JSON primeiro
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.error || `Erro ${response.status}: ${response.statusText}`);
        } else {
          throw new Error(`Erro ${response.status}: ${response.statusText}`);
        }
      }

      const data = await response.json();
      setFotos(data.fotos || []);
    } catch (error) {
      console.error('Erro ao buscar fotos:', error);
      toast({
        title: 'Erro',
        description: error instanceof Error ? error.message : 'Não foi possível carregar as fotos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (leadData.id) {
      fetchFotos();
    }
  }, [leadData.id]);

  const handleUpload = async () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      toast({
        title: 'Atenção',
        description: 'Selecione pelo menos uma imagem',
        variant: 'destructive',
      });
      return;
    }

    try {
      setUploading(true);
      console.log('📤 handleUpload - Iniciando upload:', { 
        leadId: leadData.id, 
        filesCount: selectedFiles?.length,
        descricao 
      });

      const formData = new FormData();
      Array.from(selectedFiles).forEach(file => {
        formData.append('fotos', file);
      });
      formData.append('leadId', leadData.id);
      if (descricao) {
        formData.append('descricao', descricao);
      }

      const response = await fetchWithAuth('/api/cliente-fotos/upload', {
        method: 'POST',
        body: formData,
      });
      console.log('📤 handleUpload - Resposta recebida:', { status: response.status, ok: response.ok });

      if (!response.ok) {
        // Tentar ler como JSON primeiro
        const contentType = response.headers.get('content-type');
        let errorMessage = 'Erro ao fazer upload';
        if (contentType && contentType.includes('application/json')) {
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
          } catch (e) {
            errorMessage = `Erro ${response.status}: ${response.statusText}`;
          }
        } else {
          errorMessage = `Erro ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      toast({
        title: 'Sucesso',
        description: 'Foto(s) enviada(s) com sucesso',
      });

      // Limpar formulário
      setSelectedFiles(null);
      setDescricao('');
      setUploadDialogOpen(false);
      
      // Atualizar lista
      fetchFotos();
      onUpdate();
    } catch (error) {
      console.error('Erro no upload:', error);
      toast({
        title: 'Erro',
        description: error instanceof Error ? error.message : 'Erro ao fazer upload',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (foto: Foto) => {
    try {
      const response = await fetchWithAuth(`/api/cliente-fotos/download/${foto.id}`);

      if (!response.ok) {
        throw new Error('Erro ao gerar link de download');
      }

      const data = await response.json();
      
      // Abrir URL em nova aba
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Erro ao fazer download:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível fazer o download da foto',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async () => {
    if (!fotoToDelete) return;

    try {
      const response = await fetchWithAuth(`/api/cliente-fotos/${fotoToDelete.id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Erro ao deletar foto');
      }

      toast({
        title: 'Sucesso',
        description: 'Foto deletada com sucesso',
      });

      setDeleteDialogOpen(false);
      setFotoToDelete(null);
      fetchFotos();
      onUpdate();
    } catch (error) {
      console.error('Erro ao deletar:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível deletar a foto',
        variant: 'destructive',
      });
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const openViewer = (index: number) => {
    setCurrentFotoIndex(index);
    setViewDialogOpen(true);
  };

  const handlePrevious = () => {
    if (currentFotoIndex > 0) {
      setCurrentFotoIndex(currentFotoIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentFotoIndex < fotos.length - 1) {
      setCurrentFotoIndex(currentFotoIndex + 1);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header com botão de upload */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Fotos do Cliente</h3>
          <p className="text-sm text-muted-foreground">
            Gerencie as fotos do cliente
          </p>
        </div>
        <Button onClick={() => setUploadDialogOpen(true)}>
          <Upload className="h-4 w-4 mr-2" />
          Enviar Fotos
        </Button>
      </div>

      {/* Grid de fotos com preview */}
      {fotos.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ImageIcon className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nenhuma foto encontrada</h3>
            <p className="text-muted-foreground text-center max-w-md mb-4">
              Este cliente ainda não possui fotos cadastradas. Clique no botão acima para enviar fotos.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {fotos.map((foto, index) => (
            <Card key={foto.id} className="group relative overflow-hidden hover:shadow-lg transition-all">
              {/* Preview da imagem */}
              <div 
                className="aspect-square relative bg-gray-100 cursor-pointer"
                onClick={() => openViewer(index)}
              >
                <img
                  src={foto.url_publica}
                  alt={foto.nome_original}
                  className="w-full h-full object-cover transition-transform group-hover:scale-105"
                  loading="lazy"
                />
                {/* Overlay com ícone de zoom no hover */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                  <ZoomIn className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>

              {/* Informações e ações */}
              <CardContent className="p-3">
                <div className="space-y-2">
                  <p className="text-xs font-medium truncate" title={foto.nome_original}>
                    {foto.nome_original}
                  </p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{formatFileSize(foto.tamanho_bytes)}</span>
                    <span>{formatDate(foto.created_at)}</span>
                  </div>
                  {foto.descricao && (
                    <p className="text-xs text-muted-foreground italic truncate" title={foto.descricao}>
                      "{foto.descricao}"
                    </p>
                  )}
                  <div className="flex gap-1 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(foto)}
                      className="flex-1 h-8"
                    >
                      <Download className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setFotoToDelete(foto);
                        setDeleteDialogOpen(true);
                      }}
                      className="h-8 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog de Upload */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enviar Fotos</DialogTitle>
            <DialogDescription>
              Selecione uma ou mais imagens para enviar
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição (opcional)</Label>
              <Input
                id="descricao"
                placeholder="Ex: Fotos do local"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="arquivos">Imagens</Label>
              <Input
                id="arquivos"
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => setSelectedFiles(e.target.files)}
                className="hidden"
              />
              <div className="flex items-center gap-2">
                <Label
                  htmlFor="arquivos"
                  className="cursor-pointer bg-primary/10 hover:bg-primary/20 text-primary font-medium py-2 px-4 rounded-md inline-block transition-colors"
                >
                  Escolher Imagens
                </Label>
                <div className="text-sm text-muted-foreground min-w-0 flex-1">
                  {selectedFiles && selectedFiles.length > 0 ? (
                    <span className="block whitespace-normal break-words">
                      {selectedFiles.length === 1
                        ? selectedFiles[0].name
                        : `${selectedFiles.length} imagens selecionadas`}
                    </span>
                  ) : (
                    <span>Nenhuma imagem selecionada</span>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Formatos aceitos: JPEG, PNG, GIF, WebP (máx. 5MB por imagem)
              </p>
            </div>
            {selectedFiles && selectedFiles.length > 0 && (
              <div className="bg-muted p-3 rounded-md">
                <p className="text-sm font-medium mb-2">
                  {selectedFiles.length} imagem(ns) selecionada(s):
                </p>
                <ul className="text-xs space-y-1 max-h-32 overflow-y-auto">
                  {Array.from(selectedFiles).map((file, index) => (
                    <li key={index} className="whitespace-normal break-words">
                      • {file.name} ({formatFileSize(file.size)})
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setUploadDialogOpen(false);
                setSelectedFiles(null);
                setDescricao('');
              }}
              disabled={uploading}
            >
              Cancelar
            </Button>
            <Button onClick={handleUpload} disabled={uploading || !selectedFiles}>
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Enviar
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de Visualização (Lightbox) */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] p-0">
          <div className="relative flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
              <div className="flex-1">
                <h3 className="font-semibold truncate">{fotos[currentFotoIndex]?.nome_original}</h3>
                <p className="text-sm text-muted-foreground">
                  {currentFotoIndex + 1} de {fotos.length}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setViewDialogOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Imagem */}
            <div className="flex-1 relative bg-black/5 flex items-center justify-center p-4 min-h-0">
              {fotos[currentFotoIndex] && (
                <img
                  src={fotos[currentFotoIndex].url_publica}
                  alt={fotos[currentFotoIndex].nome_original}
                  className="max-w-full max-h-full object-contain"
                  style={{ maxHeight: 'calc(90vh - 120px)' }}
                />
              )}
            </div>

            {/* Navegação - Posicionada de forma fixa */}
            {fotos.length > 1 && (
              <>
                <Button
                  variant="secondary"
                  size="icon"
                  className="absolute left-4 top-[50%] -translate-y-1/2 z-10"
                  onClick={handlePrevious}
                  disabled={currentFotoIndex === 0}
                >
                  <ChevronLeft className="h-6 w-6" />
                </Button>
                <Button
                  variant="secondary"
                  size="icon"
                  className="absolute right-4 top-[50%] -translate-y-1/2 z-10"
                  onClick={handleNext}
                  disabled={currentFotoIndex === fotos.length - 1}
                >
                  <ChevronRight className="h-6 w-6" />
                </Button>
              </>
            )}

            {/* Footer com informações */}
            {fotos[currentFotoIndex]?.descricao && (
              <div className="p-4 border-t">
                <p className="text-sm text-muted-foreground italic">
                  {fotos[currentFotoIndex].descricao}
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir a foto "{fotoToDelete?.nome_original}"?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setFotoToDelete(null)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
